import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../AuthContext';
import { API_BASE } from '../apiConfig';

/**
 * Linked accounts for the signed-in user — GET /api/users/{user_id}/linked.
 * Server item: { link_id, publisher, subscriber, link_type: 'data'|'sponsorship', verified }
 * where publisher = the player sharing data, subscriber = the provider. 204 = no links.
 * person: { name, user_id, user_email, cog_level, moca, motor_level, fulg, baselineHR, baselineGSR, colorblindness, timestamp, ability_data }
 *
 * Returns, from the signed-in user's side:
 *   players   — provider view: one entry per linked player (publisher), with the link
 *   providers — player view: one entry per linked provider (subscriber)
 *   nameOf(userId) — display name for any linked user id (falls back to null)
 * Results are cached per user for 60 s so several pages don't refetch.
 */
const cache = new Map(); // user_id -> { at, promise }
const TTL = 60_000;

export function invalidateLinked() {
    cache.clear();
}

function load(authFetch, userId, force) {
    const hit = cache.get(userId);
    if (!force && hit && Date.now() - hit.at < TTL) return hit.promise;
    const promise = authFetch(`${API_BASE}/api/users/${userId}/linked`).then(async (res) => {
        if (res.status === 204) return [];
        if (!res.ok) throw new Error(`Linked accounts didn’t load (${res.status})`);
        const data = await res.json();
        return Array.isArray(data) ? data : [];
    });
    cache.set(userId, { at: Date.now(), promise });
    promise.catch(() => cache.delete(userId));
    return promise;
}

const person = (p, link) => ({
    user_id: p?.user_id,
    name: p?.name || p?.user_email || 'Unnamed',
    email: p?.user_email || '',
    since: p?.timestamp || null,
    profile: p || {},
    link_id: link.link_id,
    link_type: link.link_type,
    verified: !!link.verified,
});

export function useLinkedPlayers() {
    const { user, authFetch } = useAuth();
    const userId = user?.user_id;
    const [state, setState] = useState({ links: [], loading: true, error: null });

    const run = useCallback((force) => {
        if (!userId) return;
        setState((s) => ({ ...s, loading: true, error: null }));
        load(authFetch, userId, force)
            .then((links) => setState({ links, loading: false, error: null }))
            .catch((e) => setState({ links: [], loading: false, error: e.message || 'Linked accounts didn’t load' }));
    }, [authFetch, userId]);

    useEffect(() => { run(false); }, [run]);

    const players = [];
    const providers = [];
    const seenP = new Set();
    const seenV = new Set();
    state.links.forEach((l) => {
        if (l.subscriber?.user_id === userId && l.publisher?.user_id && !seenP.has(l.publisher.user_id + l.link_type)) {
            seenP.add(l.publisher.user_id + l.link_type);
            players.push(person(l.publisher, l));
        }
        if (l.publisher?.user_id === userId && l.subscriber?.user_id && !seenV.has(l.subscriber.user_id + l.link_type)) {
            seenV.add(l.subscriber.user_id + l.link_type);
            providers.push(person(l.subscriber, l));
        }
    });
    const nameOf = (id) => {
        if (!id) return null;
        if (id === userId) return user?.name || null;
        const hit = players.find((p) => p.user_id === id) || providers.find((p) => p.user_id === id);
        return hit ? hit.name : null;
    };

    return { ...state, players, providers, nameOf, reload: () => run(true) };
}
